import { format, formatDistanceStrict } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Locale, dateFnsLocales } from '@global/locale/Locale'

const TICK_MS = 60_000
const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

export const useRelativeTime = (dateString: string | null | undefined): string => {
  const { t } = useTranslation()
  const lang = useSessionLanguage()
  const [, setTick] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setTick((x) => x + 1), TICK_MS)
    return () => clearInterval(interval)
  }, [])

  if (!dateString) return ''

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''

  const locale = dateFnsLocales[lang as Locale] ?? enUS
  const now = new Date()
  const diffMs = Math.max(0, now.getTime() - date.getTime())

  if (diffMs < MINUTE_MS) {
    return t('Just now', { defaultValue: 'Just now' })
  }

  const minutes = Math.floor(diffMs / MINUTE_MS)
  if (minutes < 60) {
    return formatDistanceStrict(date, now, {
      addSuffix: true,
      locale,
      roundingMethod: 'floor',
      unit: 'minute',
    })
  }

  const hours = Math.floor(diffMs / HOUR_MS)
  if (hours < 24) {
    return formatDistanceStrict(date, now, {
      addSuffix: true,
      locale,
      roundingMethod: 'floor',
      unit: 'hour',
    })
  }

  if (hours < 48) {
    return t('Yesterday', { defaultValue: 'Yesterday' })
  }

  const days = Math.floor(diffMs / DAY_MS)
  if (days < 7) {
    return formatDistanceStrict(date, now, {
      addSuffix: true,
      locale,
      roundingMethod: 'floor',
      unit: 'day',
    })
  }

  return format(date, 'dd MMM yyyy', { locale })
}
